import { currentUser, batchDataChanges } from './database';
import { services, ensureWorkspace, saveOnboarding } from './productBrain';
import { integrationsRequest, buildJiraJql } from '../lib/integrations';
import { jiraSource, jiraDestination, sourceId, sourcePayload, DESTINATIONS } from '../lib/sourceMapping';
import { activeJiraJql, isDoneJiraIssue } from '../lib/jiraPolicy';

export async function assertImportOwner(ownerId, signal) {
  if (signal?.aborted) throw new DOMException('Import cancelled. Saved records are kept; you can safely retry.', 'AbortError');
  if ((await currentUser()).id !== ownerId) throw new Error('Your SPCC account changed. Start the import again.');
}

export async function routeSource(source, destination, ownerId, { signal, priority, model } = {}) {
  if (!Object.hasOwn(DESTINATIONS, destination)) throw new Error('Choose a valid destination.');
  await assertImportOwner(ownerId, signal);
  // Existing mapping wins on re-import; explicit review may promote an Inbox item.
  if (source.mapped_type && source.mapped_type !== destination && source.mapped_type !== 'inbox') {
    throw new Error('This record is already mapped. Review it in its current destination before moving it.');
  }
  const target = services[destination];
  const id = (source.mapped_type === destination && source.mapped_id) || await sourceId(ownerId, source.source, source.source_key, destination);
  let existing = await target.getById(id);
  const payload = sourcePayload(source, destination);
  await assertImportOwner(ownerId, signal);
  if (existing) {
    // Keep the user's triage, roadmap placement, progress, and priority on re-import.
    const { title, source_metadata, description, content, source_url } = payload;
    existing = await target.update(id, { title, source_metadata, ...(destination === 'inbox' ? { content, source_url } : { description }) });
  } else existing = await target.create({ ...payload, id });
  if (destination === 'initiatives' && priority) {
    if (!['Now','Next','Later'].includes(priority.time_horizon) || !Number.isInteger(priority.priority_score) || priority.priority_score < 0 || priority.priority_score > 100) throw new Error('The AI priority is invalid. Analyze again.');
    await assertImportOwner(ownerId, signal);
    await target.update(id, { time_horizon: priority.time_horizon, priority_score: priority.priority_score,
      ai_priority: { reason: priority.reason, confidence: priority.confidence, model, reviewed_at: new Date().toISOString() } });
  }
  if (source.mapped_type === 'inbox' && destination !== 'inbox' && source.mapped_id) {
    await services.inbox.update(source.mapped_id, { status: 'processed', destination: destination === 'initiatives' ? 'initiative' : 'issue', destination_id: id });
  }
  await services.imports.update(source.id, { mapped_type: destination, mapped_id: id });
  return existing;
}

export async function saveSource(input, ownerId, { signal, legacy } = {}) {
  await assertImportOwner(ownerId, signal);
  const id = await sourceId(ownerId, input.source, input.source_key);
  const existing = await services.imports.getById(id);
  const destinationHint = input.source === 'jira' ? jiraDestination(input.metadata) : 'inbox';
  const legacyTarget = !existing && legacy?.[destinationHint]?.find(record => record.external_url === input.source_url || record.source_metadata?.source_key === input.source_key);
  const source = existing ? await services.imports.update(id, input) : await services.imports.create({ ...input, id,
    ...(legacyTarget ? { mapped_type: destinationHint, mapped_id: legacyTarget.id } : {}) });
  const destination = source.mapped_type || (source.source === 'jira' ? jiraDestination(source.metadata) : 'inbox');
  await routeSource(source, destination, ownerId, { signal });
  return destination;
}

export async function importJiraWorkspace({ projects = [], jql = 'ORDER BY key ASC', signal, onProgress = () => {}, exportData, siteUrl } = {}) {
  const ownerId = (await currentUser()).id;
  await ensureWorkspace();
  const legacy = Object.fromEntries(await Promise.all(['issues','initiatives'].map(async type => [type, await services[type].list()])));
  const counts = { total: 0, issues: 0, initiatives: 0, inbox: 0 };
  let nextPageToken, site_id;
  const seenTokens = new Set();
  const query = activeJiraJql(buildJiraJql({ projects, jql }));
  let scanned = 0;
  if (exportData && !Array.isArray(exportData.issues)) throw new Error('Choose a Jira JSON export with an issues array.');
  do {
    await assertImportOwner(ownerId, signal);
    const page = exportData ? { issues: exportData.issues.slice(scanned, scanned + 100), site_url: siteUrl, isLast: scanned + 100 >= exportData.issues.length }
      : await integrationsRequest('/api/jira/search-page', { body: { jql: query, ...(nextPageToken ? { nextPageToken } : {}), ...(site_id ? { site_id } : {}) }, signal });
    if (!Array.isArray(page.issues) || typeof page.isLast !== 'boolean') throw new Error('The integrations API needs the onboarding update. Deploy the updated Firebase Functions and try again.');
    if (site_id && page.site_id !== site_id) throw new Error('The Jira site changed. Start the import again.');
    site_id = page.site_id;
    const pageSiteId = site_id;
    scanned += page.issues.length;
    await batchDataChanges(async () => {
      for (const issue of page.issues) {
        if (isDoneJiraIssue(issue)) continue;
        const destination = await saveSource(jiraSource(issue, { site_id: pageSiteId, site_url: page.site_url }), ownerId, { signal, legacy });
        counts.total++; counts[destination]++;
        onProgress({ ...counts });
      }
    });
    if (page.isLast) break;
    nextPageToken = page.nextPageToken;
    if (!exportData && (!nextPageToken || seenTokens.has(nextPageToken))) throw new Error('Jira repeated a page. Saved records are kept; retry the import.');
    if (nextPageToken) seenTokens.add(nextPageToken);
  } while (true);
  await assertImportOwner(ownerId, signal);
  await saveOnboarding({ step: 1, last_import: { ...counts, source: 'jira', at: new Date().toISOString() } });
  return counts;
}

export async function importSlackWorkspace(rows, { teamId, channel, signal } = {}) {
  const ownerId = (await currentUser()).id;
  await ensureWorkspace();
  await batchDataChanges(async () => {
    for (const row of rows) await saveSource({ source: 'slack', source_key: `${teamId}:${channel}:${row.ext_key}`,
      source_url: '', title: row.title, content: row.summary,
      metadata: { channel, team_id: teamId, key: row.ext_key, issue_type: 'Conversation', labels: ['slack-import'] } }, ownerId, { signal });
  });
  await saveOnboarding({ last_import: { total: rows.length, source: 'slack', at: new Date().toISOString() } });
  return rows.length;
}
